import React, { ChangeEvent, KeyboardEvent, useState, useEffect } from 'react';
import { 
  TextField, 
  InputAdornment, 
  IconButton, 
  Box, 
  Tooltip, 
  Typography, 
  Alert,
  FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { motion } from 'framer-motion';
import { GlassSwitch, PyrenzDialog } from '@client/theme';

interface SearchBarProps {
  onSearch: (query: string) => void | Promise<void>;
  defaultValue?: string;
  showNSFW: boolean;
  onNSFWChange: (enabled: boolean) => Promise<void> | void;
  userUUID?: string | null;
}

export function SearchBar({ 
  onSearch, 
  defaultValue = '',
  showNSFW,
  onNSFWChange,
  userUUID 
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [openDialog, setOpenDialog] = useState(false);
  const [birthdate, setBirthdate] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  const handleSearch = async () => {
    const trimmed = inputValue.trim();
    await onSearch(trimmed);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (e.target.value.trim() === '') onSearch('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    
    if (checked && !showNSFW) {
      setOpenDialog(true);
    } else if (!checked && showNSFW) {
      handleDisableNSFW();
    }
  };

  const handleDisableNSFW = async () => {
    try {
      await onNSFWChange(false);
    } catch (err) {
      console.error('Failed to disable NSFW:', err);
    }
  };

  const calculateAge = (dob: string) => {
    if (!dob) return 0;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    
    const age = calculateAge(birthdate);
    
    if (age >= 18) {
      setIsSubmitting(true);
      try {
        await onNSFWChange(true);
        setOpenDialog(false);
        setBirthdate('');
        setError('');
      } catch (err) {
        setError('Failed to update settings. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setError('You must be at least 18 years old to enable NSFW content.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setBirthdate('');
    setError('');
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          width="100%"
        >
          <Box flex={1} maxWidth={{ xs: '100%', sm: 'calc(100% - 120px)' }}>
            <TextField
              fullWidth
              variant="outlined"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Who do you want to chat with?"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginRight: '8px' }}>
                    <IconButton
                      onClick={handleSearch}
                      edge="start"
                      sx={{ padding: '6px', color: 'grey' }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '10px',
                  backgroundColor: '#1F2937',
                  color: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '& input': { backgroundColor: 'transparent', color: 'white', padding: '0.5rem 0.5rem' },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px #1F2937 inset',
                    WebkitTextFillColor: 'white',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                },
              }}
            />
          </Box>

          <Tooltip title={userUUID ? "NSFW Switch" : "Login to enable NSFW"} arrow>
            <Box sx={{ 
              height: '32px', 
              display: 'flex', 
              alignItems: 'center',
              opacity: userUUID ? 1 : 0.5,
              pointerEvents: userUUID ? 'auto' : 'none'
            }}>
              <FormControlLabel
                control={
                  <GlassSwitch
                    checked={showNSFW}
                    onChange={handleSwitchChange}
                    disabled={!userUUID || isSubmitting}
                    inputProps={{ 'aria-label': 'NSFW switch' }}
                  />
                }
                label="NSFW"
                sx={{ 
                  color: 'white',
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      </motion.div>

      <PyrenzDialog
        open={openDialog}
        onClose={handleCancel}
        title="Age Verification Required"
        content={
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1">
              Enabling NSFW content confirms that you are 18 or older and agree to our Terms of Service. 
              Pyrenzai does not guarantee the nature of user-generated content. Please report any content 
              that violates the rules.
            </Typography>

            <TextField
              label="Enter your birthdate"
              type="date"
              value={birthdate}
              onChange={(e) => {
                setBirthdate(e.target.value);
                setError('');
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              inputProps={{
                max: new Date().toISOString().split('T')[0]
              }}
            />

            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </Box>
        }
        onConfirm={handleConfirm}
      />
    </>
  );
}